import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@apollo/client";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import {
  MARK_TASK_AS_IGNORED,
  UPDATE_TASK_STATUS,
  UPDATE_USER_TASK_COMPLETED,
} from "../../graphql/mutations";
import styles from "./SelectedActionModal.module.css";
import aiSparklesIcon from "../../assets/icons/ai-sparkles.png";
import FullScreenImageModal from "./FullScreenImageModal";
import {
  CircleCheckBigIcon,
  CircleCheckIcon,
  CircleXIcon,
  RefreshCwIcon,
} from "lucide-react";

const URL_REGEX = /https?:\/\/[^\s]+/gi;
const IMAGE_URL_REGEX = /\.(png|jpe?g|webp|gif|avif|svg)(\?|$)/i;
const LEADING_BRACKET_TAG_REGEX = /^\s*\[[^\]]+\]\s*/;

const toPlainText = (value) =>
  String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/\[object Object\]/g, "")
    .replace(/\u00a0/g, " ")
    .trim();

const isHttpUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());
const isDataImageUrl = (value) =>
  /^data:image\/[a-zA-Z0-9+.-]+;base64,/i.test(String(value || "").trim());
const isValidImageUrl = (value) =>
  isDataImageUrl(value) ||
  (isHttpUrl(value) && IMAGE_URL_REGEX.test(String(value || "").trim()));
const formatTaskTitle = (value) => {
  const clean = String(value || "").replace(LEADING_BRACKET_TAG_REGEX, "").trim();
  return clean || "Task";
};

const extractImageEntriesFromDescription = (text) => {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const entries = [];
  let section = "";

  lines.forEach((line) => {
    if (/^current homepage hero image urls\s*:$/i.test(line)) {
      section = "current";
      return;
    }
    if (/^current hero image\s*:$/i.test(line)) {
      section = "current";
      return;
    }
    if (/^pre-generated banner assets\s*:$/i.test(line)) {
      section = "suggested";
      return;
    }
    if (/^suggested hero image\s*:$/i.test(line)) {
      section = "suggested";
      return;
    }

    const matches = line.match(URL_REGEX) || [];
    matches.forEach((url) => {
      const clean = String(url || "").trim();
      if (!isValidImageUrl(clean)) return;

      const extractedLabel = line
        .replace(url, "")
        .replace(/^[-\u2022]\s*/, "")
        .replace(/\s*:\s*$/, "")
        .trim();

      const fallbackLabel =
        section === "current"
          ? "Current image"
          : section === "suggested"
            ? "Suggested image"
            : "Image";

      entries.push({
        url: clean,
        label: extractedLabel || fallbackLabel,
      });
    });
  });

  return entries;
};

const extractKnownActionImageUrls = (value, baseLabel) => {
  if (!value || typeof value !== "object") return [];
  const entries = [];
  const push = (url, label = baseLabel) => {
    const clean = String(url || "").trim();
    if (!isValidImageUrl(clean)) return;
    entries.push({ url: clean, label });
  };

  // Canonical top-level fields
  push(value?.url, baseLabel);
  push(value?.current_image_url, "Current image");
  push(value?.suggested_image_url, "Suggested image");
  push(value?.imageUrl, baseLabel);
  push(value?.upload_url, baseLabel);

  // Homepage hero structure
  if (Array.isArray(value?.hero_assessments)) {
    value.hero_assessments.forEach((hero) => push(hero?.url, "Current image"));
  }

  // Pre-generated suggestion assets
  if (value?.generated_image && typeof value.generated_image === "object") {
    push(value.generated_image?.upload_url, "Suggested image");
    push(value.generated_image?.url, "Suggested image");
  }
  if (Array.isArray(value?.generated_images)) {
    value.generated_images.forEach((img) => {
      push(img?.upload_url, "Suggested image");
      push(img?.url, "Suggested image");
    });
  }

  return entries;
};

const getImageCategory = (label = "") => {
  const normalized = String(label || "")
    .trim()
    .toLowerCase();
  if (
    normalized.includes("current") ||
    normalized.includes("hero") ||
    normalized.includes("existing") ||
    normalized.includes("old")
  ) {
    return "current";
  }
  if (
    normalized.includes("suggested") ||
    normalized.includes("variant") ||
    normalized.includes("new") ||
    normalized.includes("generated")
  ) {
    return "suggested";
  }
  return "other";
};

const stripUrlsFromLine = (line) =>
  String(line || "")
    .replace(URL_REGEX, "")
    .replace(/\s+/g, " ")
    .replace(/\s*:\s*$/, "")
    .trim();

const isPlaceholderValue = (value) =>
  /^(null|undefined|none|n\/a|-)?$/i.test(String(value || "").trim());

const toDescriptionText = (value) =>
  String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\u00a0/g, " ")
    .trim();

const sanitizeHtmlPreview = (html) =>
  String(html || "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/<(iframe|object|embed)[\s\S]*?>[\s\S]*?<\/\1>/gi, "")
    .replace(/\son\w+\s*=\s*(['"]).*?\1/gi, "")
    .replace(/\shref\s*=\s*(['"])\s*javascript:[^'"]*\1/gi, " href=\"#\"")
    .replace(/\ssrc\s*=\s*(['"])\s*javascript:[^'"]*\1/gi, "");

const toFieldLabel = (key) => {
  const normalized = String(key || "").trim();
  const map = {
    descriptionHtml: "Description (HTML)",
    descriptionText: "Description (Text)",
    alt_text_by_url: "Alt text",
    missing_alt_count: "Missing alt text count",
    seo: "SEO",
    tags: "Tags",
    outline: "Outline",
  };
  if (map[normalized]) return map[normalized];
  return normalized
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
};

const STRUCTURED_DESCRIPTION_FIELD_KEYS = new Set([
  "descriptionhtml",
  "descriptiontext",
  "alt_text_by_url",
  "alttext",
  "seo",
  "tags",
  "outline",
]);

const normalizeStructuredFieldKey = (key) =>
  String(key || "")
    .trim()
    .replace(/[\s-]+/g, "_")
    .toLowerCase();

const isKnownStructuredFieldKey = (key) =>
  STRUCTURED_DESCRIPTION_FIELD_KEYS.has(normalizeStructuredFieldKey(key));

const parseAltTextValue = (value) => {
  const text = String(value || "").trim();
  if (!text || !text.includes("current_alt_text=")) return null;
  const currentMatch = text.match(/current_alt_text=([^;]*?)(?=;\s*\w+=|$)/i);
  const suggestedMatch = text.match(
    /suggested_alt_text=([^;]*?)(?=;\s*\w+=|$)/i,
  );
  const currentAltText = currentMatch?.[1]?.trim() || "";
  const suggestedAltText = suggestedMatch?.[1]?.trim() || "";
  return {
    currentAltText,
    suggestedAltText,
  };
};

const parseSeoValue = (value) => {
  const text = String(value || "").trim();
  const titleMatch = text.match(/title=([^;]*?)(?=;\s*\w+=|$)/i);
  const descMatch = text.match(/description=([^;]*?)(?=;\s*\w+=|$)/i);
  const normalizeSeoPart = (v) => {
    const part = String(v || "").trim();
    if (!part || /^null$/i.test(part) || /^undefined$/i.test(part)) return "empty";
    return part;
  };
  if (!titleMatch && !descMatch) return null;
  return {
    metaTitle: normalizeSeoPart(titleMatch?.[1]),
    metaDescription: normalizeSeoPart(descMatch?.[1]),
  };
};

const parseTagsValue = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const suggestedMatch = text.match(/suggested=([^;]*?)(?=;\s*\w+=|$)/i);
  if (suggestedMatch) return String(suggestedMatch[1] || "").trim();
  return text;
};

const parseSimpleAltTextValue = (value) => {
  const text = String(value || "").trim();
  if (!text) return null;
  const altMatch = text.match(/altText=([^;]*?)(?=;\s*\w+=|$)/i);
  if (!altMatch) return null;
  const parsed = String(altMatch[1] || "").trim();
  if (!parsed || /^null$/i.test(parsed) || /^undefined$/i.test(parsed)) {
    return "empty";
  }
  return parsed;
};

const parseStructuredDescription = (rawDescription) => {
  const lines = toDescriptionText(rawDescription)
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+$/g, ""));

  const structured = {
    product: "",
    issue: "",
    url: "",
    current: [],
    suggested: [],
    otherLines: [],
  };

  let section = "other";
  let lastItemRef = null;

  const pushSectionItem = (targetSection, key, value) => {
    const item = {
      key: String(key || "").trim(),
      value: String(value || "").trim(),
    };
    structured[targetSection].push(item);
    lastItemRef = item;
  };

  lines.forEach((rawLine) => {
    const line = String(rawLine || "");
    const trimmed = line.trim();
    if (!trimmed) {
      if (lastItemRef && lastItemRef.value) {
        lastItemRef.value += "\n";
      }
      return;
    }

    if (/^Current:\s*$/i.test(trimmed)) {
      section = "current";
      lastItemRef = null;
      return;
    }
    if (/^Suggested:\s*$/i.test(trimmed)) {
      section = "suggested";
      lastItemRef = null;
      return;
    }

    const productMatch = trimmed.match(/^Product:\s*(.+)$/i);
    if (productMatch) {
      structured.product = productMatch[1].trim();
      section = "other";
      lastItemRef = null;
      return;
    }
    const issueMatch = trimmed.match(/^Issue:\s*(.+)$/i);
    if (issueMatch) {
      structured.issue = issueMatch[1].trim();
      section = "other";
      lastItemRef = null;
      return;
    }
    const urlMatch = trimmed.match(/^URL:\s*(.+)$/i);
    if (urlMatch) {
      structured.url = urlMatch[1].trim();
      section = "other";
      lastItemRef = null;
      return;
    }

    if (section === "current" || section === "suggested") {
      const itemMatch = line.match(/^\s*-\s*([^:]+):\s*(.*)$/);
      const inlineItemMatch = line.match(
        /^\s*([a-zA-Z][a-zA-Z0-9_\s-]{0,40}):\s*(.*)$/,
      );
      const match = itemMatch || inlineItemMatch;
      if (match) {
        const candidateKey = String(match[1] || "").trim();
        const lastKey = normalizeStructuredFieldKey(lastItemRef?.key);
        // Prevent HTML/content lines (e.g. "<strong>Origin:</strong>") from
        // being mis-parsed as a new field, which truncates descriptionHtml.
        if (
          candidateKey.includes("<") ||
          candidateKey.includes(">") ||
          (lastKey === "descriptionhtml" &&
            !isKnownStructuredFieldKey(candidateKey))
        ) {
          if (lastItemRef) {
            const separator = lastItemRef.value.endsWith("\n") ? "" : "\n";
            lastItemRef.value += `${separator}${trimmed}`;
            return;
          }
        }
        pushSectionItem(section, match[1], match[2]);
        return;
      }
      if (lastItemRef) {
        const separator = lastItemRef.value.endsWith("\n") ? "" : "\n";
        lastItemRef.value += `${separator}${trimmed}`;
        return;
      }
    }

    structured.otherLines.push(trimmed);
  });

  const hasSections = structured.current.length > 0 || structured.suggested.length > 0;
  const hasHeader = structured.product || structured.issue || structured.url;
  if (!hasSections && !hasHeader) return null;
  return structured;
};

const isAiTask = (taskNode) => {
  const assigned =
    typeof taskNode?.assignedTo === "string"
      ? taskNode.assignedTo.trim().toLowerCase()
      : "";
  return assigned !== "" && assigned !== "user";
};

const isUserTask = (taskNode) => {
  const assigned =
    typeof taskNode?.assignedTo === "string"
      ? taskNode.assignedTo.trim().toLowerCase()
      : "";
  return assigned === "user";
};

const normalizeTaskId = (taskNode, fallbackIndex) =>
  String(taskNode?.id || taskNode?._id || fallbackIndex || "");

const normalizeActionId = (actionNode) =>
  String(actionNode?.id || actionNode?._id || actionNode?.action_id || "");

const normalizeTitle = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isReindexGateAction = (actionTitle = "") => {
  const title = normalizeTitle(actionTitle);
  const hasTestLiveUrl = title.includes("test live url");
  const hasReindex =
    title.includes("reindex") || title.includes("request soft reindex");
  return hasTestLiveUrl && hasReindex;
};

const SelectedActionTasks = ({
  action,
  groupActions = [],
  enableTaskActions = false,
  activeTab,
  onClose,
  onActionCompleted,
  onRunTask,
}) => {
  const { user } = useAuthUser();
  const { t } = useLocalization();
  const authToken = user?.token;
  const userId = action?.user_id || user?._id || user?.id || user?.userId;
  const [runLoadingId, setRunLoadingId] = useState(null);
  const [completeLoadingId, setCompleteLoadingId] = useState(null);
  const [skipLoadingId, setSkipLoadingId] = useState(null);
  const [reenableLoadingId, setReenableLoadingId] = useState(null);
  const [runAllLoading, setRunAllLoading] = useState(false);
  const [taskOverrides, setTaskOverrides] = useState({});
  const [ignoreModalTask, setIgnoreModalTask] = useState(null);
  const [ignoreReasonInput, setIgnoreReasonInput] = useState("");
  const [ignoreModalError, setIgnoreModalError] = useState("");
  const [runTaskStatus] = useMutation(UPDATE_TASK_STATUS);
  const [markTaskIgnored] = useMutation(MARK_TASK_AS_IGNORED);
  const [completeTask] = useMutation(UPDATE_USER_TASK_COMPLETED);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  // Filter tasks based on tab - hide Running tasks in Suggested tab
  const isRunningTab = activeTab === t("ZeqiKeyFocusArea.tabs.running");
  const isRunningTask = (taskNode) =>
    String(taskNode?.status || "").toLowerCase() === "running";
  const isCompletedTask = (taskNode) => taskNode?.isCompleted === true;
  const isPendingTask = (taskNode) =>
    !isCompletedTask(taskNode) && !isRunningTask(taskNode);

  const filteredTasks = Array.isArray(action?.tasks)
    ? isRunningTab
      ? action.tasks
      : action.tasks.filter((task) => isPendingTask(task))
    : [];

  const tasks = filteredTasks;

  const hasMixedTasks = useMemo(() => {
    const hasAi = tasks.some((t) => isAiTask(t));
    const hasUser = tasks.some((t) => isUserTask(t));
    return hasAi && hasUser;
  }, [tasks]);
  const allUserTasks = useMemo(
    () => tasks.length > 0 && tasks.every((t) => isUserTask(t)),
    [tasks],
  );
  const hasAutomatedTasks = useMemo(
    () => tasks.some((t) => isAiTask(t)),
    [tasks],
  );
  const showTaskActions =
    enableTaskActions && (hasMixedTasks || allUserTasks || hasAutomatedTasks);
  const isSuggestedTab = activeTab === t("ZeqiKeyFocusArea.tabs.suggested");
  const isReindexAction = useMemo(
    () => isReindexGateAction(action?.title || ""),
    [action?.title],
  );
  const isReindexRunBlocked = useMemo(() => {
    if (!isSuggestedTab || !isReindexAction) return false;
    const currentId = String(action?.id || "");
    const peers = Array.isArray(groupActions) ? groupActions : [];
    return peers.some((peer) => {
      if (!peer) return false;
      if (String(peer?.id || "") === currentId) return false;
      return !isReindexGateAction(peer?.title || "");
    });
  }, [action?.id, groupActions, isReindexAction, isSuggestedTab]);
  const showActionFooter =
    isSuggestedTab && !hasMixedTasks && !allUserTasks && hasAutomatedTasks;
  const actionDescriptionRawText = useMemo(
    () => toDescriptionText(action?.description || ""),
    [action?.description],
  );
  const actionStructuredDescription = useMemo(
    () => parseStructuredDescription(actionDescriptionRawText),
    [actionDescriptionRawText],
  );
  const actionDescriptionText = useMemo(
    () => toPlainText(actionDescriptionRawText),
    [actionDescriptionRawText],
  );
  const actionDescriptionLines = useMemo(
    () =>
      actionDescriptionText
        .split(/\r?\n/)
        .map((line) => stripUrlsFromLine(line))
        .filter((line) => !/^-?\s*current_image\s*:?\s*$/i.test(line))
        .filter((line) => !/^-?\s*current hero image\s*:?\s*$/i.test(line))
        .filter((line) => !/^-?\s*suggested hero image\s*:?\s*$/i.test(line))
        .filter((line) => !/^-?\s*current homepage hero image urls\s*:?\s*$/i.test(line))
        .filter((line) => !/^-?\s*pre-generated banner assets\s*:?\s*$/i.test(line))
        .filter((line) => !isPlaceholderValue(line))
        .filter(Boolean),
    [actionDescriptionText],
  );
  const actionImageEntries = useMemo(() => {
    const byCurrent = extractKnownActionImageUrls(
      action?.current,
      "Current image",
    );
    const bySuggestion = extractKnownActionImageUrls(
      action?.suggestion,
      "Suggested image",
    );
    const byDescription = extractImageEntriesFromDescription(
      actionDescriptionText,
    );

    const merged = [];
    const seen = new Set();
    [...byCurrent, ...bySuggestion, ...byDescription].forEach((entry) => {
      const key = String(entry?.url || "").trim();
      if (!key) return;
      const dedupeKey = key;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      merged.push(entry);
    });

    let currentCount = 0;
    let suggestedCount = 0;
    let otherCount = 0;

    return merged.map((entry) => {
      const category = getImageCategory(entry?.label);
      if (category === "current") {
        currentCount += 1;
        return {
          ...entry,
          label: currentCount === 1 ? "Current image" : `Current image ${currentCount}`,
        };
      }
      if (category === "suggested") {
        suggestedCount += 1;
        return {
          ...entry,
          label:
            suggestedCount === 1 ? "Suggested image" : `Suggested image ${suggestedCount}`,
        };
      }
      otherCount += 1;
      return { ...entry, label: `Image ${otherCount}` };
    });
  }, [action, actionDescriptionText]);

  // Get the selected image object based on the index (must be after actionImageEntries)
  const selectedImage =
    selectedImageIndex !== null ? actionImageEntries[selectedImageIndex] : null;

  const openMainChannelDiscussion = (content, context) => {
    const seedId = `action-discussion:${Date.now()}:${Math.random()
      .toString(36)
      .slice(2)}`;
    const chatTabLabel = t("ZeqiMainTab.chat") || "Chat";

    try {
      window.sessionStorage.setItem(
        "zeqi:pendingChatSeed",
        JSON.stringify({ content, seedId, createdAt: Date.now() }),
      );
    } catch {
      // ignore unavailable storage
    }

    window.dispatchEvent(
      new CustomEvent("zeqi:set-active-channel", {
        detail: { channelId: "main", subchannelId: "" },
      }),
    );
    window.dispatchEvent(
      new CustomEvent("zeqi:channel-changed", {
        detail: { channelId: "main", subchannelId: "" },
      }),
    );
    window.dispatchEvent(
      new CustomEvent("zeqi:switch-tab", { detail: { tab: chatTabLabel } }),
    );

    window.setTimeout(() => {
      if (context?.key) {
        window.dispatchEvent(
          new CustomEvent("zeqi:add-chat-context", {
            detail: { channelId: "main", context },
          }),
        );
      }
      window.dispatchEvent(
        new CustomEvent("zeqi:chat-seed", {
          detail: { content, seedId },
        }),
      );
    }, 75);
  };

  const handleDiscuss = () => {
    const title =
      action.title ||
      action.department ||
      t("SelectedSuggestedActionModal.actionFallback") ||
      "Action";
    const description = action.description ? String(action.description) : "";
    const prefix = t("SelectedSuggestedActionModal.actionPrefix") || "Action:";
    const content = `${prefix} ${title}\n${description}`.trim();
    const actionId = normalizeActionId(action);
    const context = {
      label: title,
      type: "action",
      key: actionId ? `action:${actionId}` : `action:${title}`,
      actionId: actionId || null,
      status: action?.status || null,
      collectionName: action?.collectionName || action?.collection_name || null,
      reportId: action?.reportId || action?.report_id || null,
      description,
    };
    onClose?.();
    openMainChannelDiscussion(content, context);
  };

  const handleRunAll = async () => {
    if (runAllLoading) return;
    if (!authToken) {
      alert(
        t("common.authRequiredBody", {
          defaultValue: "Please log in again to run this task.",
        }),
      );
      return;
    }

    setRunAllLoading(true);
    try {
      for (const task of tasks) {
        const id = normalizeTaskId(task);
        if (!id) continue;
        await runTaskStatus({
          variables: { taskId: id, userId, status: "Running" },
          context: {
            headers: { Authorization: `Bearer ${authToken}` },
          },
        });
      }
      // Notify parent that action should be removed from the list
      onActionCompleted?.(action?.id);
      // Notify parent to set this action as the one to expand
      onRunTask?.(action?.id);
    } catch (err) {
      console.error("Failed to run action:", err?.message || err);
      alert(
        t("common.unknownError", {
          defaultValue: "Something went wrong. Please try again.",
        }),
      );
    } finally {
      setRunAllLoading(false);
    }
  };

  const handleDiscussTask = (taskNode) => {
    const taskTitle = formatTaskTitle(
      taskNode?.title || taskNode?.task || taskNode?.name,
    );
    const content =
      `${taskTitle}\n${
        taskNode?.description ||
        taskNode?.short_description ||
        t("ZeqiChatModal.defaultDiscussPrompt") ||
        "Let's discuss this task."
      }`.trim();
    const actionId = normalizeActionId(action);
    const taskId = normalizeTaskId(taskNode);
    const context = {
      label: taskTitle,
      type: "task",
      key: taskId ? `task:${taskId}` : `task:${taskTitle}`,
      taskId: taskId || null,
      actionId: actionId || taskNode?.action_id || null,
      status: taskNode?.status || null,
      collectionName:
        taskNode?.collectionName ||
        taskNode?.collection_name ||
        action?.collectionName ||
        action?.collection_name ||
        null,
      reportId:
        taskNode?.reportId ||
        taskNode?.report_id ||
        action?.reportId ||
        action?.report_id ||
        null,
      description: taskNode?.description || taskNode?.short_description || "",
    };
    onClose?.();
    openMainChannelDiscussion(content, context);
  };

  const _handleRunTask = async (taskNode) => {
    const id = normalizeTaskId(taskNode);
    if (!id || runLoadingId === id) return;
    if (!authToken) {
      alert(
        t("common.authRequiredBody", {
          defaultValue: "Please log in again to run this task.",
        }),
      );
      return;
    }
    setRunLoadingId(id);
    const nextOverrides = { ...taskOverrides, [id]: { status: "Running", isCompleted: false } };
    setTaskOverrides(nextOverrides);
    try {
      await runTaskStatus({
        variables: { taskId: id, userId, status: "Running" },
        optimisticResponse: {
          updateTaskStatus: {
            __typename: "CompleteTaskPayload",
            success: true,
            message: "Optimistic: running…",
            task: {
              __typename: "Task",
              id,
              status: "Running",
              isCompleted: false,
              updatedAt: new Date().toISOString(),
            },
          },
        },
        update: (cache, { data }) => {
          const changed = data?.updateTaskStatus?.task;
          if (!changed?.id) return;
          const cacheId = cache.identify({
            __typename: "Task",
            id: String(changed.id),
          });
          if (!cacheId) return;
          cache.modify({
            id: cacheId,
            fields: {
              status: () => changed.status,
              isCompleted: () => changed.isCompleted,
              updatedAt: () => changed.updatedAt,
            },
          });
        },
        context: {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      });
      // Only remove the action when this was the last pending task.
      // If other tasks are still pending, keep the action visible so the user can run them.
      const hasMorePending = tasks.some((t) => {
        const tId = normalizeTaskId(t);
        if (tId === id) return false;
        const tOverride = nextOverrides[tId];
        const tStatus = tOverride?.status ?? t?.status ?? null;
        const tCompleted = tOverride?.isCompleted ?? t?.isCompleted;
        return !tCompleted && String(tStatus || "").toLowerCase() !== "running" && tStatus !== "Ignored";
      });
      if (!hasMorePending) {
        onActionCompleted?.(action?.id);
      }
      onRunTask?.(action?.id);
    } catch (err) {
      console.error("Failed to run task:", err?.message || err);
      alert(
        t("common.unknownError", {
          defaultValue: "Something went wrong. Please try again.",
        }),
      );
      setTaskOverrides((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } finally {
      setRunLoadingId(null);
    }
  };

  const handleCompleteTask = async (taskNode) => {
    const id = normalizeTaskId(taskNode);
    if (!id || completeLoadingId) return;

    if (!isUserTask(taskNode)) {
      alert(
        t("SelectedSuggestedActionSection.notAllowedTitle"),
        t("SelectedSuggestedActionSection.notAllowedMsg"),
      );
      return;
    }

    const isApproved = taskNode?.isApproved !== false;
    if (!isApproved) {
      alert(
        t("SelectedSuggestedActionSection.notApprovedTitle"),
        t("SelectedSuggestedActionSection.notApprovedMsg"),
      );
      return;
    }

    if (taskNode?.isCompleted) return;

    setCompleteLoadingId(id);
    setTaskOverrides((prev) => ({
      ...prev,
      [id]: { status: "Running", isCompleted: true },
    }));
    try {
      await completeTask({
        variables: { taskId: id, userId },
        optimisticResponse: {
          updateUserTaskWhenCompleted: {
            __typename: "CompleteTaskPayload",
            success: true,
            message: "Optimistic: completed…",
            taskId: id,
            actionId: taskNode?.action_id ?? null,
            rollup: {
              __typename: "RollupSummary",
              totalTasks: 0,
              completedTasks: 0,
              allCompleted: false,
            },
            task: {
              __typename: "Task",
              id,
              status: "Running",
              isCompleted: true,
              updatedAt: new Date().toISOString(),
            },
          },
        },
        update: (cache, { data }) => {
          const changed = data?.updateUserTaskWhenCompleted?.task;
          if (!changed?.id) return;
          const cacheId = cache.identify({
            __typename: "Task",
            id: String(changed.id),
          });
          if (!cacheId) return;
          cache.modify({
            id: cacheId,
            fields: {
              status: () => changed.status,
              isCompleted: () => changed.isCompleted,
              updatedAt: () => changed.updatedAt,
            },
          });
        },
        context: {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      });
    } catch (err) {
      console.error("Failed to complete task:", err?.message || err);
      alert(
        t("SelectedSuggestedActionSection.completeErrorTitle"),
        err?.message || t("SelectedSuggestedActionSection.unknownError"),
      );
      setTaskOverrides((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } finally {
      setCompleteLoadingId(null);
    }
  };

  const closeIgnoreModal = () => {
    if (skipLoadingId) return;
    setIgnoreModalTask(null);
    setIgnoreReasonInput("");
    setIgnoreModalError("");
  };

  const forceCloseIgnoreModal = () => {
    setIgnoreModalTask(null);
    setIgnoreReasonInput("");
    setIgnoreModalError("");
  };

  const openIgnoreModal = (taskNode) => {
    if (!taskNode || skipLoadingId) return;
    setIgnoreModalTask(taskNode);
    setIgnoreReasonInput("");
    setIgnoreModalError("");
  };

  const handleSkipTask = async () => {
    const taskNode = ignoreModalTask;
    const id = normalizeTaskId(taskNode);
    if (!id || skipLoadingId) return;

    const ignoreReason = String(ignoreReasonInput || "").trim();
    if (!ignoreReason) {
      setIgnoreModalError(
        t("SelectedSuggestedActionSection.skipReasonRequired") ||
          "A reason is required to ignore this task.",
      );
      return;
    }

    if (!authToken) {
      setIgnoreModalError(
        t("common.authRequiredBody", {
          defaultValue: "Please log in again to skip this task.",
        }),
      );
      return;
    }

    if (taskNode?.isCompleted) return;

    setSkipLoadingId(id);
    setTaskOverrides((prev) => ({
      ...prev,
      [id]: { status: "Ignored", isCompleted: true },
    }));

    try {
      await markTaskIgnored({
        variables: { taskId: id, userId, reason: ignoreReason },
        optimisticResponse: {
          markTaskAsIgnored: {
            __typename: "CompleteTaskPayload",
            success: true,
            message: "Optimistic: skipped…",
            task: {
              __typename: "Task",
              id,
              status: "Ignored",
              isCompleted: true,
              updatedAt: new Date().toISOString(),
            },
          },
        },
        update: (cache, { data }) => {
          const changed = data?.markTaskAsIgnored?.task;
          if (!changed?.id) return;
          const cacheId = cache.identify({
            __typename: "Task",
            id: String(changed.id),
          });
          if (!cacheId) return;
          cache.modify({
            id: cacheId,
            fields: {
              status: () => changed.status,
              isCompleted: () => changed.isCompleted,
              updatedAt: () => changed.updatedAt,
            },
          });
        },
        context: {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      });
      // Notify parent that action should be removed from the list
      onActionCompleted?.(action?.id);
      forceCloseIgnoreModal();
    } catch (err) {
      console.error("Failed to skip task:", err?.message || err);
      setIgnoreModalError(
        t("common.unknownError", {
          defaultValue: "Something went wrong. Please try again.",
        }),
      );
      setTaskOverrides((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } finally {
      setSkipLoadingId(null);
    }
  };

  const handleReenableTask = async (taskNode) => {
    const id = normalizeTaskId(taskNode);
    if (!id || reenableLoadingId) return;

    if (!authToken) {
      alert(
        t("common.authRequiredBody", {
          defaultValue: "Please log in again to re-enable this task.",
        }),
      );
      return;
    }

    // Remove the override to revert to original state
    setTaskOverrides((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    setReenableLoadingId(id);
    try {
      await runTaskStatus({
        variables: { taskId: id, userId, status: "Suggested" },
        optimisticResponse: {
          updateTaskStatus: {
            __typename: "CompleteTaskPayload",
            success: true,
            message: "Optimistic: re-enabled…",
            task: {
              __typename: "Task",
              id,
              status: "Suggested",
              isCompleted: false,
              updatedAt: new Date().toISOString(),
            },
          },
        },
        update: (cache, { data }) => {
          const changed = data?.updateTaskStatus?.task;
          if (!changed?.id) return;
          const cacheId = cache.identify({
            __typename: "Task",
            id: String(changed.id),
          });
          if (!cacheId) return;
          cache.modify({
            id: cacheId,
            fields: {
              status: () => changed.status,
              isCompleted: () => changed.isCompleted,
              updatedAt: () => changed.updatedAt,
            },
          });
        },
        context: {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      });
    } catch (err) {
      console.error("Failed to re-enable task:", err?.message || err);
      alert(
        t("common.unknownError", {
          defaultValue: "Something went wrong. Please try again.",
        }),
      );
    } finally {
      setReenableLoadingId(null);
    }
  };

  const renderStructuredItems = (items, sectionKey) =>
    items.map((item, idx) => {
      const value = String(item?.value || "").trim();
      const key = String(item?.key || "").trim();
      if (key.toLowerCase() === "outline") return null;
      const label = toFieldLabel(key);
      const altTextData =
        key === "alt_text_by_url" ? parseAltTextValue(value) : null;
      const seoData = key.toLowerCase() === "seo" ? parseSeoValue(value) : null;
      const tagsValue = key.toLowerCase() === "tags" ? parseTagsValue(value) : null;
      const simpleAltTextValue =
        key.toLowerCase() === "alttext" ? parseSimpleAltTextValue(value) : null;

      if (
        key === "descriptionHtml" &&
        value &&
        value !== "null" &&
        value !== "undefined"
      ) {
        return (
          <div key={`${sectionKey}-${idx}`} className={styles.actionDataItem}>
            <p className={styles.actionDataLabel}>{label}</p>
            <div
              className={styles.actionHtmlPreview}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtmlPreview(value),
              }}
            />
          </div>
        );
      }

      if (altTextData) {
        return (
          <div key={`${sectionKey}-${idx}`} className={styles.actionDataItem}>
            <p className={styles.actionDataLabel}>{label}</p>
            {altTextData.currentAltText && (
              <p className={styles.actionDescriptionLine}>
                <strong>Current alt text:</strong> {altTextData.currentAltText}
              </p>
            )}
            {altTextData.suggestedAltText && (
              <p className={styles.actionDescriptionLine}>
                <strong>Suggested alt text:</strong> {altTextData.suggestedAltText}
              </p>
            )}
          </div>
        );
      }
      if (seoData) {
        return (
          <p key={`${sectionKey}-${idx}`} className={styles.actionDescriptionLine}>
            <strong>{label}:</strong> meta title = {seoData.metaTitle}; meta description ={" "}
            {seoData.metaDescription}
          </p>
        );
      }
      if (tagsValue !== null) {
        return (
          <p key={`${sectionKey}-${idx}`} className={styles.actionDescriptionLine}>
            <strong>{label}:</strong> {tagsValue || "empty"}
          </p>
        );
      }
      if (simpleAltTextValue !== null) {
        return (
          <p key={`${sectionKey}-${idx}`} className={styles.actionDescriptionLine}>
            <strong>Alt text:</strong> {simpleAltTextValue}
          </p>
        );
      }

      return (
        <p key={`${sectionKey}-${idx}`} className={styles.actionDescriptionLine}>
          <strong>{label}:</strong> {value || "-"}
        </p>
      );
    });

  return (
    <>
      <div className={styles.taskView}>
        {(actionDescriptionLines.length > 0 ||
          actionImageEntries.length > 0 ||
          actionStructuredDescription) && (
          <div className={styles.actionDescription}>
            {actionStructuredDescription ? (
              <div className={styles.actionDescriptionText}>
                {actionStructuredDescription.product && (
                  <p className={styles.actionDescriptionLine}>
                    <strong>Product:</strong> {actionStructuredDescription.product}
                  </p>
                )}
                {actionStructuredDescription.issue && (
                  <p className={styles.actionDescriptionLine}>
                    <strong>Issue:</strong> {actionStructuredDescription.issue}
                  </p>
                )}
                {actionStructuredDescription.url && (
                  <p className={styles.actionDescriptionLine}>
                    <strong>URL:</strong>{" "}
                    <a
                      href={actionStructuredDescription.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.actionDescriptionLink}
                    >
                      {actionStructuredDescription.url}
                    </a>
                  </p>
                )}
                {actionStructuredDescription.current.length > 0 && (
                  <div className={styles.actionDataSection}>
                    <p className={styles.actionDataTitle}>
                      <strong>Current</strong>
                    </p>
                    {renderStructuredItems(actionStructuredDescription.current, "current")}
                  </div>
                )}
                {actionStructuredDescription.suggested.length > 0 && (
                  <div className={styles.actionDataSection}>
                    <p className={styles.actionDataTitle}>
                      <strong>Suggested</strong>
                    </p>
                    {renderStructuredItems(
                      actionStructuredDescription.suggested,
                      "suggested",
                    )}
                  </div>
                )}
                {actionStructuredDescription.otherLines.map((line, idx) => (
                  (() => {
                    const sanitized = stripUrlsFromLine(line);
                    if (
                      /^-?\s*current hero image\s*:?\s*$/i.test(sanitized) ||
                      /^-?\s*suggested hero image\s*:?\s*$/i.test(sanitized) ||
                      isPlaceholderValue(sanitized)
                    ) {
                      return null;
                    }
                    return (
                      <p key={`action-other-${idx}`} className={styles.actionDescriptionLine}>
                        {sanitized}
                      </p>
                    );
                  })()
                ))}
              </div>
            ) : (
              <div className={styles.actionDescriptionText}>
                {actionDescriptionLines.map((line, idx) => (
                  <p
                    key={`action-desc-${idx}`}
                    className={styles.actionDescriptionLine}
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}
            {actionImageEntries.length > 0 && (
              <div className={styles.actionImageGrid}>
                {actionImageEntries.map((entry, idx) => (
                  <div
                    key={`${entry.label}-${idx}`}
                    className={styles.actionImageCard}
                  >
                    <img
                      src={entry.url}
                      alt={entry.label}
                      className={styles.actionImagePreview}
                      loading="lazy"
                      onClick={() => setSelectedImageIndex(idx)}
                      style={{ cursor: "pointer" }}
                    />
                    <span className={styles.actionImageLabel}>
                      {entry.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className={styles.taskGroup}>
          <span className={styles.taskLabel}>Tasks</span>
          {tasks.length > 0 ? (
            <ul className={styles.taskList}>
              {tasks.map((task, idx) => {
                const id = normalizeTaskId(task, idx);
                const override = taskOverrides[id];
                const status = override?.status || task?.status || null;
                const isCompleted = override?.isCompleted ?? task?.isCompleted;
                const isIgnored = status === "Ignored";
                const isTaskRunning = String(status || "").toLowerCase() === "running";
                return (
                  <li
                    key={id}
                    className={`${styles.taskItem} ${isIgnored ? styles.taskItemIgnored : ""}`}
                  >
                    <div className={styles.taskItemContent}>
                      <div className={styles.taskItemHeader}>
                        <span className={styles.taskTitle}>
                          {formatTaskTitle(task.task_title)}
                        </span>
                        {status && (
                          <span
                            className={`${styles.taskStatus} ${
                              isIgnored
                                ? styles.taskStatusIgnored
                                : isCompleted
                                  ? styles.taskStatusCompleted
                                  : status === "Running"
                                    ? styles.taskStatusRunning
                                    : ""
                            }`}
                          >
                            {isIgnored
                              ? t("SelectedSuggestedActionSection.skipped") ||
                                "Skipped"
                              : isCompleted
                                ? "Completed"
                                : status === "Suggested"
                                  ? "Pending"
                                  : status === "Ignored"
                                    ? "Rejected"
                                    : status}
                          </span>
                        )}
                      </div>
                      {task.short_description && (
                        <span className={styles.taskDescription}>
                          {task.short_description}
                        </span>
                      )}
                    </div>
                    {showTaskActions && isAiTask(task) ? (
                      <div
                        className={`${styles.taskCardActions} ${styles.taskCardActionsCompact}`}
                      >
                        {isIgnored ? (
                          <button
                            type="button"
                            className={`${styles.taskCardButton} ${styles.iconActionButton} ${styles.reenableButton}`}
                            onClick={() => handleReenableTask(task)}
                            disabled={reenableLoadingId === id}
                            title="Re-enable task"
                          >
                            {reenableLoadingId === id
                              ? t("SelectedSuggestedActionModal.runningNow") ||
                                "Running…"
                              : "+"}
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className={`${styles.taskCardButton} ${styles.taskRunButton}`}
                              onClick={() => _handleRunTask(task)}
                              disabled={
                                runLoadingId === id ||
                                Boolean(taskOverrides[id]) ||
                                isCompleted ||
                                isTaskRunning
                              }
                              title={
                                runLoadingId === id
                                  ? t("SelectedSuggestedActionModal.runningNow") ||
                                    "Running…"
                                  : isTaskRunning
                                    ? t("SelectedSuggestedActionModal.runningNow") ||
                                      "Running…"
                                  : t("SelectedSuggestedActionModal.runThisTask") ||
                                    "Run this task"
                              }
                            >
                              {runLoadingId === id ? (
                                <RefreshCwIcon
                                  size={18}
                                  className={styles.loadingSpinner}
                                />
                              ) : (
                                <>
                                  <img
                                    src={aiSparklesIcon}
                                    alt=""
                                    className={styles.buttonIcon}
                                  />
                                  <span>
                                    {t("SelectedSuggestedActionModal.runThisTask") ||
                                      "Run This Task"}
                                  </span>
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              className={`${styles.taskCardButton} ${styles.iconActionButton} ${styles.skipButton}`}
                              onClick={() => openIgnoreModal(task)}
                              disabled={skipLoadingId === id || isCompleted || isTaskRunning}
                              title={
                                t(
                                  "SelectedSuggestedActionSection.skipConfirmTitle",
                                ) || "Skip this task"
                              }
                            >
                              {skipLoadingId === id ? (
                                t("SelectedSuggestedActionModal.runningNow") ||
                                "Running…"
                              ) : (
                                <CircleXIcon size={18} />
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    ) : showTaskActions && !isAiTask(task) ? (
                      <div className={styles.taskCardActions}>
                        <button
                          type="button"
                          className={`${styles.taskCardButton} ${styles.discussButton}`}
                          onClick={() => handleDiscussTask(task)}
                          title={
                            t("SelectedSuggestedActionModal.discuss") ||
                            "Discuss"
                          }
                        >
                          <img
                            src={aiSparklesIcon}
                            alt={
                              t("SelectedSuggestedActionModal.discuss") ||
                              "Discuss"
                            }
                            className={styles.buttonIcon}
                          />
                        </button>
                        <button
                          type="button"
                          className={`${styles.taskCardButton} ${styles.completeButton}`}
                          onClick={() => handleCompleteTask(task)}
                          disabled={completeLoadingId === id || isCompleted}
                          title={
                            completeLoadingId === id
                              ? t("SelectedSuggestedActionModal.runningNow") ||
                                "Running…"
                              : isCompleted
                                ? t(
                                    "SelectedSuggestedActionSection.completed",
                                  ) || "Completed"
                                : t(
                                    "SelectedSuggestedActionSection.markAsDone",
                                  ) || "Mark as Done"
                          }
                        >
                          {completeLoadingId === id ? (
                            <RefreshCwIcon
                              size={18}
                              className={styles.loadingSpinner}
                            />
                          ) : isCompleted ? (
                            <CircleCheckIcon size={18} />
                          ) : (
                            <CircleCheckBigIcon size={18} />
                          )}
                        </button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className={styles.noTasks}>No tasks available.</p>
          )}
        </div>
        {showActionFooter && (
          <div className={styles.accordionFooter}>
            <button
              type="button"
              className={`${styles.footerButton} ${styles.discussButton} ${
                isRunningTab ? styles.footerButtonFull : ""
              }`}
              onClick={handleDiscuss}
            >
              <img src={aiSparklesIcon} alt="" className={styles.buttonIcon} />
              {t("SelectedSuggestedActionModal.discuss") || "Discuss"}
            </button>
            {isSuggestedTab && (
              <button
                type="button"
                className={`${styles.footerButton} ${styles.runButton}`}
                onClick={handleRunAll}
                disabled={runAllLoading || isReindexRunBlocked}
                title={
                  isReindexRunBlocked
                    ? "Run all other actions/tasks for this product or blog first."
                    : undefined
                }
              >
                {runAllLoading
                  ? t("SelectedSuggestedActionModal.runningNow") || "Running…"
                  : t("SelectedSuggestedActionModal.runThisTask") ||
                    "Run This Task"}
              </button>
            )}
          </div>
        )}
      </div>

      {ignoreModalTask && typeof document !== "undefined"
        ? createPortal(
            <div
              className={styles.ignoreModalBackdrop}
              role="presentation"
              onClick={closeIgnoreModal}
            >
              <div
                className={styles.ignoreModalCard}
                role="dialog"
                aria-modal="true"
                aria-labelledby="ignore-task-modal-title"
                onClick={(event) => event.stopPropagation()}
              >
                <h3
                  id="ignore-task-modal-title"
                  className={styles.ignoreModalTitle}
                >
                  {t("SelectedSuggestedActionSection.skipConfirmTitle") ||
                    "Skip this task?"}
                </h3>
                <p className={styles.ignoreModalBody}>
                  {t("SelectedSuggestedActionSection.skipConfirmMessage") ||
                    "This task will be marked as ignored and hidden from your active queue."}
                </p>
                <label
                  className={styles.ignoreModalLabel}
                  htmlFor="ignore-task-reason"
                >
                  {t("SelectedSuggestedActionSection.skipReasonPrompt") ||
                    "Please tell us why you're ignoring this task."}
                </label>
                <textarea
                  id="ignore-task-reason"
                  className={styles.ignoreModalInput}
                  value={ignoreReasonInput}
                  onChange={(event) => {
                    setIgnoreReasonInput(event.target.value);
                    if (ignoreModalError) setIgnoreModalError("");
                  }}
                  placeholder={
                    t("SelectedSuggestedActionSection.skipReasonPlaceholder") ||
                    "Enter your reason..."
                  }
                  rows={4}
                  maxLength={500}
                  autoFocus
                  disabled={Boolean(skipLoadingId)}
                />
                {ignoreModalError ? (
                  <p className={styles.ignoreModalError}>{ignoreModalError}</p>
                ) : null}
                <div className={styles.ignoreModalActions}>
                  <button
                    type="button"
                    className={`${styles.ignoreModalButton} ${styles.ignoreModalCancelButton}`}
                    onClick={closeIgnoreModal}
                    disabled={Boolean(skipLoadingId)}
                  >
                    {t("common.cancel") || "Cancel"}
                  </button>
                  <button
                    type="button"
                    className={`${styles.ignoreModalButton} ${styles.ignoreModalConfirmButton}`}
                    onClick={handleSkipTask}
                    disabled={Boolean(skipLoadingId)}
                  >
                    {skipLoadingId
                      ? t("SelectedSuggestedActionModal.runningNow") ||
                        "Running…"
                      : t("SelectedSuggestedActionSection.skip") ||
                        "Ignore Task"}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      <FullScreenImageModal
        isOpen={selectedImageIndex !== null}
        imageUrl={selectedImage?.url}
        imageLabel={selectedImage?.label}
        images={actionImageEntries}
        currentIndex={selectedImageIndex}
        onClose={() => setSelectedImageIndex(null)}
        onNext={() => setSelectedImageIndex((prev) => prev + 1)}
        onPrevious={() => setSelectedImageIndex((prev) => prev - 1)}
      />
    </>
  );
};

export default SelectedActionTasks;
